import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppBar, Toolbar, Typography, IconButton, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { getItem } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Selectors';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';
import ToolbarList from '@configuratorware/configurator-frontendgui/App/Shared/Components/ToolbarList';
import SyncIcon from '@material-ui/icons/Sync';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { setDisplayNetPrices } from '../../Reducers/Configurator/Actions';
import { subHeaderTan } from '../../ThemeProvider';
import { isOnDemandPrice } from '../../Reducers/Configurator/Selectors';

export interface CustomHeaderProps {
  /** Ref set by the designer screen with the real-size PDF handler (print to PDF). */
  onPrintToPdfRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}

const useStyles = makeStyles((theme) => {
  const w: Record<string, unknown> = ((theme as { weha?: unknown }).weha as Record<string, unknown>) || {};
  const header = w.header || {};
  return {
    headerWrapper: {
      position: 'relative',
    },
    headerShadow: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 8,
      pointerEvents: 'none',
      boxShadow: w.shadowHeader || 'none',
    },
    appBar: {
      backgroundColor: ((header as Record<string, unknown>).backgroundColor as string) || '#fff',
      color: ((header as Record<string, unknown>).titleColor as string) || theme.palette.primary.main,
      borderBottom:
        ((header as Record<string, unknown>).borderBottom as string) || `1px solid ${theme.palette.divider}`,
    },
    toolbar: {
      minHeight: 64,
      padding: '0 20px',
    },
    title: {
      color: ((header as Record<string, unknown>).titleColor as string) || theme.palette.primary.main,
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    grow: {
      flexGrow: 1,
    },
    toolsWrapper: {
      color: ((header as Record<string, unknown>).iconColor as string) || theme.palette.text.secondary,
      '& .material-icons': {
        color: ((header as Record<string, unknown>).iconColor as string) || theme.palette.text.secondary,
      },
    },
    iconButton: {
      color: ((header as Record<string, unknown>).iconColor as string) || theme.palette.text.secondary,
    },
    iconButtonNet: {
      color: (w.subHeaderTan as string) || subHeaderTan,
    },
  };
});

/** Build header tools: print to PDF (first), upload, download. Separator and moon are rendered separately. */
function buildHeaderTools(onPrintToPdfRef: React.MutableRefObject<(() => Promise<void>) | null> | undefined) {
  const tools = [
    {
      key: 'printToPdf',
      identifier: 'print',
      title: t('weha.header.printToPdf'),
      function: () => onPrintToPdfRef?.current?.(),
      enabled: true,
    },
    {
      key: 'load',
      identifier: 'cloud_upload',
      title: 'headerMenu.load',
      function: () => Services.interaction.showLoadConfigurationDialog(),
      enabled: getConf('availableTools.load'),
    },
    {
      key: 'save',
      identifier: 'cloud_download',
      title: 'headerMenu.save',
      function: () => Services.interaction.saveConfiguration(),
      enabled: getConf('availableTools.save'),
    },
  ].filter((t) => t.enabled);

  return tools;
}

export default function CustomHeader({ onPrintToPdfRef }: CustomHeaderProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isOnDemandPriceFlag = useSelector(isOnDemandPrice);
  const title = useSelector((state: { configurator?: unknown }) => {
    const item = getItem(state.configurator);
    return (item && (item as { title?: string }).title) ?? '';
  });
  const displayNetPrices = useSelector(
    (state: { configurator?: { displayNetPrices?: boolean } }) =>
      state.configurator?.displayNetPrices ?? false
  );
  const tools = React.useMemo(() => buildHeaderTools(onPrintToPdfRef), [onPrintToPdfRef]);

  const handleNetGrossToggle = React.useCallback(() => {
    dispatch(setDisplayNetPrices(!displayNetPrices));
  }, [dispatch, displayNetPrices]);

  return (
    <div className={classes.headerWrapper}>
      <AppBar position="static" className={classes.appBar} elevation={0}>
        <Toolbar className={classes.toolbar}>
          <Typography className={classes.title} variant="h6" component="h1">
            {title || t('weha.header.defaultTitle')}
          </Typography>
          <div className={classes.grow} />
          <div className={classes.toolsWrapper} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <ToolbarList tools={tools} />
            <Divider orientation="vertical" flexItem style={{ marginLeft: 8, marginRight: 8 }} />
            <IconButton
              className={displayNetPrices ? classes.iconButtonNet : classes.iconButton}
              onClick={handleNetGrossToggle}
              title={t('weha.header.netGrossToggle')}
              aria-label={t('weha.header.netGrossToggle')}
              size="small"
              disableRipple
              disableFocusRipple
              disabled={isOnDemandPriceFlag}
            >
              <SyncIcon />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>
      <div className={classes.headerShadow} aria-hidden />
    </div>
  );
}
